import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  Portal,
  useOutsideClick,
} from '@chakra-ui/react'
import { format, isValid, parse } from 'date-fns'
import {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useKey } from 'react-use'

import '../../../theme/dates/DateInput.css'
import { dateToDateString, parseDate } from '../../../utils/dates'
import { Icon } from '../../Icons/Icon'
import { BaseInput } from '../BaseInput'
import { TextInputProps } from '../TextInput/TextInput'
import {
  CustomCalendarDayContents,
  CustomCalendarHeader,
} from './DateInput.parts'

export type DateInputProps = Omit<TextInputProps, 'onChange'> & {
  /**
   * ISO compatible date string as input
   */
  value?: string | null
  onChange?: (date: string) => void
  calendarPortalRef?: any
  isOpen?: boolean
  /**
   * Default is true
   *
   * If operating within a Modal, consider setting to false.
   */
  usePortal?: boolean
  /** When true, hide the calendar icon (e.g. when used inside a composite range input) */
  hideCalendarIcon?: boolean
}

const elementIsChildOf = (
  element: HTMLElement,
  parent: HTMLElement,
): boolean => {
  if (element === parent) return true
  if (!element?.parentElement) return false
  return elementIsChildOf(element?.parentElement, parent)
}

export const isoToAmerican = (isoDate: string) => {
  const parsed = IsoToDate(isoDate)
  if (!parsed) return ''

  return dateToAmerican(parsed!)
}

const IsoToDate = (isoDate: string) => {
  const parsed = parseDate(isoDate)
  if (!isValid(parsed)) return null

  return parsed
}

export const americanToIso = (americanDate: string) => {
  const parsed = americanToDate(americanDate)
  if (!parsed) return ''

  return dateToIso(parsed!)
}
const dateToAmerican = (date: Date) => {
  return format(date, 'MM-dd-yyyy')
}

const americanToDate = (americanDate: string) => {
  const parsed = parse(americanDate, 'MM-dd-yyyy', new Date())
  if (!isValid(parsed)) return null

  return parsed
}

export const dateToIso = (date: Date) => {
  return dateToDateString(date)
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      value = '',
      onChange,
      placeholder = 'mm-dd-yyyy',
      onBlur,
      onFocus,
      usePortal = true,
      hideCalendarIcon = false,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLInputElement | null>(null)
    const popoverContentRef = useRef<HTMLElement | null>(null)

    useImperativeHandle(ref, () => internalRef.current!)

    const [inputValue, setInputValue] = useState<string>(() => value)
    const selectedDate = americanToDate(inputValue)

    useEffect(() => {
      if (inputValue !== value) {
        setInputValue(value)
      }
    }, [value])

    const [isFocused, setIsFocused] = useState(false)

    useOutsideClick({
      ref: popoverContentRef as any,
      enabled: Boolean(popoverContentRef) && isFocused,
      handler: event => {
        if (
          elementIsChildOf(
            event.target as HTMLElement,
            internalRef.current?.parentElement as HTMLElement,
          )
        ) {
          return
        }
        setIsFocused(false)
      },
    })

    useKey(
      'Escape',
      () => {
        setIsFocused(false)
        setTimeout(() => {
          internalRef.current?.focus()
        }, 0)
      },
      {
        target: popoverContentRef.current,
      },
    )
    useKey('Escape', () => setIsFocused(false), {
      target: internalRef.current,
    })

    const handleCalendarDateChange = (date: Date | null) => {
      if (!internalRef.current) return
      const newValue = date ? dateToAmerican(date) : ''

      setInputValue(newValue)
      onChange?.(newValue)
      setIsFocused(false)
    }

    const ConditionalPortal = usePortal ? Portal : Fragment

    return (
      <Popover
        isLazy
        isOpen={isFocused}
        closeOnBlur={false}
        returnFocusOnClose={false}
        autoFocus={false}
      >
        <PopoverAnchor>
          <BaseInput
            ref={instance => {
              internalRef.current = instance
              if (typeof ref === 'function') ref(instance)
            }}
            onFocus={event => {
              if (
                elementIsChildOf(
                  event.relatedTarget as HTMLElement,
                  popoverContentRef.current as HTMLElement,
                )
              ) {
                return
              }

              setIsFocused(true)
              onFocus?.(event)
            }}
            onBlur={event => {
              if (
                elementIsChildOf(
                  event.relatedTarget as HTMLElement,
                  popoverContentRef.current as HTMLElement,
                )
              ) {
                return
              }

              setIsFocused(false)

              onBlur?.(event)
            }}
            data-testid="date-input"
            value={inputValue}
            onChange={event => {
              setInputValue(event.target.value)

              // const newValue = americanToIso(event.target.value)
              onChange?.(event.target.value)
            }}
            placeholder={placeholder}
            rightInputElement={
              hideCalendarIcon ? undefined : (
                <Icon
                  icon="CalendarIcon"
                  boxSize="12px"
                  color="presets.neutral.60"
                />
              )
            }
            {...props}
          />
        </PopoverAnchor>
        <ConditionalPortal>
          <PopoverContent ref={popoverContentRef}>
            <ReactDatePicker
              enableTabLoop={false}
              popperPlacement="bottom"
              inline
              open
              data-testid="date-input-calendar"
              selected={selectedDate}
              onChange={handleCalendarDateChange}
              showPopperArrow={false}
              renderCustomHeader={CustomCalendarHeader}
              renderDayContents={CustomCalendarDayContents}
              formatWeekDay={day => day[0]}
            />
          </PopoverContent>
        </ConditionalPortal>
      </Popover>
    )
  },
)
